export type ApiClientOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
};

export type ApiResponse<T = unknown> = {
  data: T;
  response: Response;
};

// グローバルなユーザー情報管理
let globalUserId: string | null = null;

/**
 * グローバルユーザーIDを設定する関数
 * アプリケーションの初期化時またはログイン時に呼び出す
 */
export const setGlobalUserId = (userId: string) => {
  globalUserId = userId;
};

/**
 * 現在のグローバルユーザーIDを取得する関数
 */
export const getGlobalUserId = (): string | null => {
  return globalUserId;
};

const BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://back.gxp21.com/api';

// ========================================
// 共通ヘルパー関数
// ========================================

/**
 * APIエンドポイントのURLを構築する
 * @param endpoint - APIエンドポイント
 * @param additionalParams - 追加のクエリパラメータ
 * @returns 完全なURL
 */
const buildApiUrl = (endpoint: string, additionalParams?: Record<string, string>): string => {
  let url = endpoint.startsWith('/') ? `${BASE_URL}${endpoint}` : `${BASE_URL}/${endpoint}`;

  // グローバルユーザーIDを取得してクエリパラメータに追加
  const userId = getGlobalUserId() || '';
  const separator = url.includes('?') ? '&' : '?';
  url += `${separator}userId=${encodeURIComponent(userId)}`;

  // 追加パラメータがあれば追加
  if (additionalParams) {
    Object.entries(additionalParams).forEach(([key, value]) => {
      url += `&${key}=${encodeURIComponent(value)}`;
    });
  }

  return url;
};

/**
 * デフォルトヘッダーを生成する
 * @param acceptType - Acceptヘッダーの値
 * @param includeContentType - Content-Typeを含めるか
 * @param accessToken - 認証トークン
 * @returns ヘッダーオブジェクト
 */
const buildDefaultHeaders = (
  acceptType: string = 'application/json',
  includeContentType: boolean = true,
  accessToken?: string
): Record<string, string> => {
  const headers: Record<string, string> = {
    'Accept': acceptType,
  };

  if (includeContentType) {
    headers['Content-Type'] = 'application/json';
  }

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  return headers;
};

/**
 * 共通のfetchオプションを構築する
 * @param method - HTTPメソッド
 * @param headers - ヘッダー
 * @param body - リクエストボディ
 * @returns RequestInit
 */
const buildFetchOptions = (
  method: string,
  headers: Record<string, string>,
  body?: unknown
): RequestInit => {
  const options: RequestInit = {
    mode: 'cors',
    method,
    headers,
    credentials: 'omit',
  };

  if (body !== undefined && (method === 'PUT' || method === 'POST')) {
    options.body = typeof body === 'string' ? body : JSON.stringify(body);
  }

  return options;
};

/**
 * 共通のAPIクライアント関数
 * @param endpoint - エンドポイント（例: '/getIrais', '/updateIrai'）
 * @param options - リクエストオプション
 * @param accessToken - 認証トークン
 * @returns Promise<ApiResponse<T>>
 */
export const apiClient = async <T = unknown>(
  endpoint: string,
  options: ApiClientOptions = {},
  accessToken?: string
): Promise<ApiResponse<T>> => {
  const { method = 'GET', body, headers = {} } = options;

  const url = buildApiUrl(endpoint);
  const defaultHeaders = buildDefaultHeaders('application/json', true, accessToken);
  const mergedHeaders = { ...defaultHeaders, ...headers };
  const fetchOptions = buildFetchOptions(method, mergedHeaders, body);

  try {
    const response = await fetch(url, fetchOptions);

    // レスポンスが空の場合の処理
    const contentType = response.headers.get('content-type');
    let data: T;

    if (contentType && contentType.includes('application/json')) {
      const text = await response.text();
      data = text.trim() ? JSON.parse(text) : null;
    } else {
      data = await response.text() as unknown as T;
    }

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}, statusText: ${response.statusText}`);
    }

    return { data, response };
  } catch (error) {
    console.error(`API request failed: ${method} ${url}`, error);
    throw error;
  }
};

/**
 * GETリクエスト専用のヘルパー関数
 */
export const apiGet = <T = unknown>(endpoint: string, accessToken?: string, customHeaders?: Record<string, string>): Promise<ApiResponse<T>> => {
  return apiClient<T>(endpoint, { method: 'GET', headers: customHeaders }, accessToken);
};

/**
 * POSTリクエスト専用のヘルパー関数
 */
export const apiPost = <T = unknown>(endpoint: string, body: unknown, accessToken?: string, customHeaders?: Record<string, string>): Promise<ApiResponse<T>> => {
  return apiClient<T>(endpoint, { method: 'POST', body, headers: customHeaders }, accessToken);
};

/**
 * PUTリクエスト専用のヘルパー関数
 */
export const apiPut = <T = unknown>(endpoint: string, body: unknown, accessToken?: string, customHeaders?: Record<string, string>): Promise<ApiResponse<T>> => {
  return apiClient<T>(endpoint, { method: 'PUT', body, headers: customHeaders }, accessToken);
};

/**
 * DELETEリクエスト専用のヘルパー関数
 */
export const apiDelete = <T = unknown>(endpoint: string, accessToken?: string, customHeaders?: Record<string, string>): Promise<ApiResponse<T>> => {
  return apiClient<T>(endpoint, { method: 'DELETE', headers: customHeaders }, accessToken);
};

/**
 * PDFファイル取得専用のヘルパー関数（POST）
 */
export const apiPostPdf = async (endpoint: string, body: unknown, accessToken?: string, customHeaders?: Record<string, string>, additionalParams?: Record<string, string>): Promise<Blob> => {
  const url = buildApiUrl(endpoint, additionalParams);
  const defaultHeaders = buildDefaultHeaders('application/pdf', true, accessToken);
  const mergedHeaders = { ...defaultHeaders, ...(customHeaders || {}) };
  const fetchOptions = buildFetchOptions('POST', mergedHeaders, body);

  try {
    const response = await fetch(url, fetchOptions);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}, statusText: ${response.statusText}`);
    }

    return await response.blob();
  } catch (error) {
    console.error(`PDF API request failed: POST ${url}`, error);
    throw error;
  }
};

/**
 * PDFファイル取得専用のGETヘルパー関数
 */
export const apiGetPdf = async (endpoint: string, iraiId: string, accessToken?: string, customHeaders?: Record<string, string>): Promise<Blob> => {
  const url = buildApiUrl(endpoint, { id: iraiId });
  const defaultHeaders = buildDefaultHeaders('application/pdf', false, accessToken);
  const mergedHeaders = { ...defaultHeaders, ...(customHeaders || {}) };
  const fetchOptions = buildFetchOptions('GET', mergedHeaders);

  try {
    const response = await fetch(url, fetchOptions);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}, statusText: ${response.statusText}`);
    }

    return await response.blob();
  } catch (error) {
    console.error(`PDF GET API request failed: GET ${url}`, error);
    throw error;
  }
};

/**
 * APIクライアントのエラーハンドリング用のユーティリティ関数
 */
export const handleApiError = (error: unknown, context: string = 'API request'): string => {
  console.error(`${context} failed:`, error);

  if (error instanceof TypeError) {
    return `Network error: ${error.message}`;
  } else if (error instanceof Error) {
    return error.message;
  } else {
    return `${context} failed with unknown error`;
  }
};
